import {createApi} from '@reduxjs/toolkit/query/react'
import {baseQueryWithReAuth} from "@/store/features/baseRtkQuery";

// Types
interface AgentAccessUser {
    userId: string;
    grantedAt: string;
    grantedBy: string;
}

interface AgentAccessGroup {
    groupId: string;
    grantedAt: string;
    grantedBy: string;
}

interface AgentAccess {
    users: AgentAccessUser[];
    groups: AgentAccessGroup[];
}

interface AgentAccessUpdateResponse {
    success: boolean;
    message: string;
    agentId: string;
    access: {
        users: AgentAccessUser[];
        groups: AgentAccessGroup[];
    };
    summary: {
        totalUsers: number;
        totalGroups: number;
        previousUsers: number;
        previousGroups: number;
        changesApplied: number;
    };
}

interface AvailableUser {
    id: string;
    displayName: string;
    mail: string;
}

interface AvailableGroup {
    id: string;
    displayName: string;
    description?: string;
}

interface SecuritySubgroup {
}

interface SecuritySubgroup {
    id: string;
    parentGroupId: string;
    subgroupName: string;
    subgroupCode: string;
    description?: string;
    isActive: boolean;
}

interface SecurityGroup {
    id: string;
    groupName: string;
    groupCode: string;
    description?: string;
    isActive: boolean;
    subgroups: SecuritySubgroup[];
}

export const agentAccessApiSlice = createApi({
    reducerPath: 'agentAccessApi',
    baseQuery: baseQueryWithReAuth,
    tagTypes: ['AgentAccess', 'AvailableUsers', 'AvailableGroups'] as const,
    endpoints: (builder) => ({
        // Obtener todos los usuarios disponibles de Azure AD
        getAvailableUsers: builder.query<AvailableUser[], void>({
            query: () => ({
                url: 'api/agent-access/users',
                method: 'GET',
            }),
            providesTags: ['AvailableUsers'],
        }),

        // Obtener todos los grupos disponibles de Azure AD
        getAvailableGroups: builder.query<SecurityGroup[], void>({
            query: () => ({
                url: 'api/agent-access/groups',
                method: 'GET',
            }),
            providesTags: ['AvailableGroups'],
        }),

        // Obtener usuarios y grupos con acceso a un agente
        getAgentAccess: builder.query<AgentAccess, string>({
            query: (agentId) => ({
                url: `api/agent-access/${agentId}`,
                method: 'GET',
            }),
            providesTags: (result, error, agentId) => [
                {type: 'AgentAccess', id: agentId}
            ],
        }),

        // Establecer usuarios y grupos con acceso a un agente (reemplaza todo)
        setAgentAccess: builder.mutation<AgentAccessUpdateResponse, {
            agentId: string;
            userIds?: string[];
            groupIds?: string[];
        }>({
            query: ({agentId, userIds, groupIds}) => ({
                url: `api/agent-access/${agentId}`,
                method: 'PUT',
                body: {
                    userIds: userIds || [],
                    groupIds: groupIds || []
                },
            }),
            // Invalidar el cache para forzar recarga
            invalidatesTags: (result, error, {agentId}) => [
                {type: 'AgentAccess', id: agentId}
            ],
            // Opcionalmente: Update optimista
            async onQueryStarted({agentId, userIds, groupIds}, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    agentAccessApiSlice.util.updateQueryData('getAgentAccess', agentId, (draft) => {
                        // Actualización optimista del cache
                        draft.users = (userIds || []).map(userId => ({
                            userId,
                            grantedAt: new Date().toISOString(),
                            grantedBy: 'current-user' // Temporal
                        }));
                        draft.groups = (groupIds || []).map(groupId => ({
                            groupId,
                            grantedAt: new Date().toISOString(),
                            grantedBy: 'current-user' // Temporal
                        }));
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
    }),
})

// Export hooks
export const {
    useGetAvailableUsersQuery,
    useGetAvailableGroupsQuery,
    useGetAgentAccessQuery,
    useSetAgentAccessMutation,
} = agentAccessApiSlice

// Export actions object for consistency with your pattern
export const agentAccessApiActions = {
    useGetAvailableUsersQuery,
    useGetAvailableGroupsQuery,
    useGetAgentAccessQuery,
    useSetAgentAccessMutation,
}